/**
 * Shared types for the proposal / contract domain.
 * Both ProposalCreate/Edit and ContractCreate/Edit should import from here
 * instead of defining local versions.
 */

export type Phase = 'proposal' | 'contract'

export type ContractType = 'one_time' | 'recurring' | 'as_needed'

/** True only for auto-scheduled recurring agreements (visit board / job generation). */
export function isScheduledRecurring(
  contractType: ContractType | string | null | undefined
): boolean {
  return contractType === 'recurring'
}

/** One-time projects and ongoing/as-needed share usage-style units & billing filters. */
export function usesOneTimeStylePricing(
  contractType: ContractType | string | null | undefined
): boolean {
  return contractType === 'one_time' || contractType === 'as_needed'
}

export function formatContractTypeLabel(
  contractType: ContractType | string | null | undefined
): string {
  switch (contractType) {
    case 'recurring':
      return 'Recurring'
    case 'as_needed':
      return 'Ongoing (As Needed)'
    case 'one_time':
      return 'One-Time'
    default:
      return contractType ? String(contractType).replace(/_/g, ' ') : ''
  }
}

export type ServiceType =
  | 'maintenance'
  | 'dsny'
  | 'maintenance_dsny'
  | 'maintenance_prevailing_wage'
  | 'dsny_prevailing_wage'
  | 'construction_cleaning'
  | 'apartment_turnovers'
  | 'office_cleaning'
  | 'hourly_clean'
  | 'cleaning'
  | 'landscaping'
  | 'security'
  | 'custom'
  | ''

export type UnitType =
  | 'per_month'
  | 'per_visit'
  | 'per_hour'
  | 'per_sqft'
  | 'per_unit'
  | 'per_apartment'
  | 'per_bedroom'
  | 'per_bathroom'
  | 'flat_rate'

export type RecurrenceType =
  | 'one_time'
  | 'daily'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'custom'
  | 'custom_interval'
  | ''

export type IntervalUnit =
  | 'days_per_week'
  | 'days_per_month'
  | 'every_x_weeks'
  | 'every_x_months'
  | ''

export type BillingFrequency =
  | 'one_time'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'after_each_service'
  | 'progress'

export type BillingMethod = 'per_period' | 'divide_total' | 'actual_usage' | 'progress' | ''

export type MilestoneStatus = 'pending' | 'invoiced' | 'paid'

export interface BillingMilestone {
  id: string
  proposal_id?: string | null
  contract_id?: string | null
  label: string
  percentage: number
  amount: number
  sort_order: number
  status: MilestoneStatus
  invoice_id?: string | null
  invoiced_at?: string | null
  notes?: string | null
}

export type PaymentMethod = 'cash' | 'check' | 'ach' | 'credit_card' | ''

export type TemplateType =
  | 'maintenance'
  | 'cleaning'
  | 'landscaping'
  | 'security'
  | 'dsny'
  | 'custom'
  | ''

/**
 * A single line item on a proposal or service on a contract.
 * The two are similar enough that we keep them in one shape for the rules engine.
 */
export interface LineItem {
  id: string
  service_item_id?: string
  description: string
  quantity: number
  is_variable: boolean
  unit_type: UnitType | string
  unit_price: number
  total_price: number
  sort_order?: number
  // recurrence
  recurrence_type?: RecurrenceType | string
  recurrence_interval?: number
  interval_unit?: IntervalUnit | string
  recurrence_days?: number[]
  service_type?: ServiceType | string
  // dsny
  dsny_integration?: boolean
  dsny_pickup_type?: string
}

/** Who furnishes supplies / equipment in the generated document. */
export type FurnishedBy = 'company' | 'customer'

/** How line items are presented in the document's compensation section. */
export type PricingMode = 'total' | 'rate_card'

/** One row of the structured Work Specifications table. */
export interface WorkSpecRow {
  id: string
  group: string // e.g. "Interior", "Exterior", "Cleaning Specifications", "Standard Procedures"
  task: string
  frequency?: string // e.g. "6 days/week", "As needed", or "" for checklist items
}

/** Per-section include/override for the generated document. */
export interface DocumentSectionOverride {
  id: string // section id, e.g. "general", "compensation", "snow"
  included: boolean
  textOverride?: string // when set, replaces the template default body text
}

/** Snow removal pricing block (maintenance / prevailing wage). */
export interface SnowPricing {
  enabled: boolean
  thresholdInches: number // e.g. 8
  overRate: number // $ per snow at or above threshold
  underRate: number // $ per snow below threshold
  includedCount: number // number of below-threshold snowfalls included in the base price (0 = none). A future auto-counter should count only snowfalls below thresholdInches.
}

/** Additional / special-services labor rate and how it is billed. */
export interface AdditionalWork {
  rate: number
  unit: string // 'per_hour' | 'per_apartment' | 'per_unit' | 'flat' | free text
}

/** Hourly crew rate option (a crew of N at a combined rate). */
export interface CrewRate {
  size: number
  rate: number
}

/** Prevailing-wage specific extras. */
export interface PrevailingWageExtras {
  rateExpiryDate?: string | null // ISO date; rendered as "Until <date>"
  ptoHolidayBilledSeparately: boolean
}

/**
 * Everything configurable about the generated proposal/contract document.
 * Stored as the `document_data` JSONB column; prefilled per service type and
 * editable per document. All fields optional so partially-filled rows are fine.
 */
export interface DocumentData {
  sections?: DocumentSectionOverride[]
  workSpecs?: WorkSpecRow[]
  additionalWork?: AdditionalWork | null
  suppliesBy?: FurnishedBy
  equipmentBy?: FurnishedBy
  snow?: SnowPricing | null
  crew?: CrewRate | null
  pw?: PrevailingWageExtras | null
  autoRenew?: boolean
  pricingMode?: PricingMode
}

/**
 * Top-level agreement state passed to checkConsistency().
 */
export interface AgreementState {
  contract_type: ContractType
  service_type?: ServiceType | string
  billing_frequency?: BillingFrequency | string
  billing_method?: BillingMethod | string
  payment_method?: PaymentMethod | string
  template_type?: TemplateType | string
  start_date?: string | null
  end_date?: string | null
  property_address?: string
  line_items: LineItem[]
  billing_milestones?: BillingMilestone[]
  pricing_mode?: PricingMode
  // DSNY context (contract phase mostly)
  dsny_integration?: boolean
  garbage_schedule?: number[]
  recycling_schedule?: number[]
  organics_schedule?: number[]
  bulk_schedule?: number[]
  // visit groups (contract phase only)
  visit_groups?: VisitGroupState[]
}

export interface VisitGroupState {
  id: string
  name?: string
  start_date?: string | null
  end_date?: string | null
  recurrence_type?: RecurrenceType | string
  recurrence_days?: number[]
  service_ids?: string[]
}

/**
 * A consistency issue produced by the rules engine.
 * level=warn shows a yellow message; level=block prevents save.
 * `target` is used by the "Fix this" navigation buttons.
 */
export type IssueLevel = 'warn' | 'block'

export interface IssueTarget {
  tab: string
  anchor?: string
  field?: string
}

export interface ConsistencyIssue {
  code: string
  level: IssueLevel
  message: string
  target: IssueTarget
}
